import { useState, useEffect } from 'react';
import api, { handleApiError, queryParams } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDateTime, statusBadgeClass, statusLabel } from '../utils/formatters';
import { Truck, Search, Plus, ChevronLeft, ChevronRight, FileText, Building2, Warehouse } from 'lucide-react';
import toast from 'react-hot-toast';

function ReceiptRow({ receipt, onView }) {
  return (
    <tr>
      <td>
        <span className="font-medium text-gray-900 dark:text-white">{receipt.gr_number || receipt.number || '-'}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{formatDateTime(receipt.date)}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{receipt.purchase_order?.po_number || receipt.po_number || '-'}</span>
      </td>
      <td>
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-700 dark:text-gray-200">{receipt.supplier?.name || receipt.purchase_order?.supplier?.name || '-'}</span>
        </div>
      </td>
      <td>
        <div className="flex items-center gap-2">
          <Warehouse className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-700 dark:text-gray-200">{receipt.warehouse?.name || receipt.purchase_order?.warehouse?.name || '-'}</span>
        </div>
      </td>
      <td>
        <span className={`badge ${statusBadgeClass(receipt.status)}`}>{statusLabel(receipt.status)}</span>
      </td>
      <td>
        <button onClick={() => onView(receipt)} className="p-1.5 text-primary-900 hover:bg-primary-50 dark:hover:bg-primary-950/50 rounded">
          <FileText className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
}

export default function PurchaseReceiptsPage() {
  const { hasPermission } = useAuth();
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ search: '', status: '', date_from: '', date_to: '' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [receiptItems, setReceiptItems] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchReceipts();
  }, [pagination.page, filters]);

  const fetchReceipts = async () => {
    setLoading(true);
    try {
      const params = queryParams({ page: pagination.page, limit: pagination.limit, ...filters });
      const res = await api.get('/goods-receipts', { params });
      if (res.data.success) {
        setReceipts(res.data.data?.goods_receipts || res.data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: res.data.data?.total || 0,
          totalPages: res.data.data?.totalPages || Math.ceil((res.data.data?.total || 0) / prev.limit),
        }));
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailablePOs = async () => {
    try {
      const res = await api.get('/purchase-orders', { params: { status: 'sent', limit: 50 } });
      setPurchaseOrders(res.data.data?.purchase_orders || res.data.data || []);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handlePOSelect = (poId) => {
    const po = purchaseOrders.find(p => p.id === parseInt(poId));
    if (po) {
      setSelectedPO(po);
      setReceiptItems((po.items || []).map(item => ({
        product_id: item.product_id,
        product_name: item.product_name || item.product?.name,
        sku: item.sku || item.product?.sku,
        ordered_qty: item.qty,
        qty_received: item.qty,
        unit_price: item.unit_price || item.price
      })));
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!selectedPO) {
      toast.error('Pilih PO terlebih dahulu');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        purchase_order_id: selectedPO.id,
        items: receiptItems.map(item => ({
          product_id: item.product_id,
          qty_received: parseFloat(item.qty_received),
          unit_price: parseFloat(item.unit_price)
        }))
      };
      const res = await api.post('/goods-receipts', payload);
      if (res.data.success) {
        toast.success('Penerimaan barang berhasil dicatat');
        setShowCreateModal(false);
        setSelectedPO(null);
        setReceiptItems([]);
        fetchReceipts();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const updateReceivedQty = (index, value) => {
    const newItems = [...receiptItems];
    newItems[index].qty_received = parseFloat(value) || 0;
    setReceiptItems(newItems);
  };

  const totalQtyReceived = () => {
    return receiptItems.reduce((sum, item) => sum + (parseFloat(item.qty_received) || 0), 0);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Penerimaan Barang</h1>
          <p className="page-subtitle">Catat penerimaan barang dari purchase order</p>
        </div>
        {hasPermission('create_goods_receipts') && (
          <button onClick={() => { fetchAvailablePOs(); setShowCreateModal(true); }} className="btn-primary">
            <Plus className="h-4 w-4 mr-2" /> Buat Penerimaan Baru
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className="input pl-9"
                placeholder="Cari nomor GR, PO..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && fetchReceipts()}
              />
            </div>
            <div>
              <select
                className="input"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">Semua Status</option>
                <option value="draft">Draft</option>
                <option value="completed">Selesai</option>
                <option value="cancelled">Dibatalkan</option>
              </select>
            </div>
            <div>
              <input
                type="date"
                className="input"
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              />
            </div>
            <div>
              <input
                type="date"
                className="input"
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              />
            </div>
            <div className="flex gap-2">
              <button onClick={fetchReceipts} className="btn-primary btn-sm">Cari</button>
              <button onClick={() => setFilters({ search: '', status: '', date_from: '', date_to: '' })} className="btn-secondary btn-sm">Reset</button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="card-body">
            <div className="space-y-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="flex gap-4 items-center">
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-20" />
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-28" />
                  <div className="skeleton h-4 w-28" />
                  <div className="skeleton h-4 w-16" />
                </div>
              ))}
            </div>
          </div>
        ) : receipts.length === 0 ? (
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <Truck className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Data</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Belum ada penerimaan barang.</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Nomor GR</th>
                    <th>Tanggal</th>
                    <th>PO No.</th>
                    <th>Supplier</th>
                    <th>Gudang</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {receipts.map((r) => (
                    <ReceiptRow
                      key={r.id}
                      receipt={r}
                      onView={() => { setSelectedReceipt(r); setShowDetailsModal(true); }}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Menampilkan {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} dari {pagination.total} data
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))} disabled={pagination.page <= 1} className="btn btn-sm btn-outline">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-700 dark:text-gray-300 px-2">Halaman {pagination.page} / {pagination.totalPages}</span>
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))} disabled={pagination.page >= pagination.totalPages} className="btn btn-sm btn-outline">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="bg-white/95 dark:bg-surface-900/95 rounded-3xl border border-white/80 dark:border-white/10 shadow-modal backdrop-blur-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Buat Penerimaan Barang</h3>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="label">Pilih Purchase Order <span className="text-danger-600">*</span></label>
                <select className="input" value={selectedPO?.id || ''} onChange={(e) => handlePOSelect(e.target.value)} required>
                  <option value="">Pilih PO (status: Terkirim)</option>
                  {purchaseOrders.map(po => (
                    <option key={po.id} value={po.id}>
                      {po.po_number || po.number} - {po.supplier?.name} (Total: {formatCurrency(po.total)})
                    </option>
                  ))}
                </select>
              </div>

              {selectedPO && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="label">Supplier:</span>
                      <p className="text-gray-900 dark:text-white">{selectedPO.supplier?.name}</p>
                    </div>
                    <div>
                      <span className="label">Gudang:</span>
                      <p className="text-gray-900 dark:text-white">{selectedPO.warehouse?.name}</p>
                    </div>
                  </div>

                  {receiptItems.length > 0 && (
                    <div className="table-container">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Produk</th>
                            <th>SKU</th>
                            <th>Qty PO</th>
                            <th>Qty Diterima</th>
                            <th>Harga Satuan</th>
                          </tr>
                        </thead>
                        <tbody>
                          {receiptItems.map((item, index) => (
                            <tr key={index}>
                              <td>{item.product_name}</td>
                              <td>{item.sku}</td>
                              <td>{item.ordered_qty}</td>
                              <td>
                                <input
                                  type="number"
                                  className="input w-24"
                                  value={item.qty_received}
                                  onChange={(e) => updateReceivedQty(index, e.target.value)}
                                  min="0"
                                  max={item.ordered_qty}
                                  step="1"
                                />
                              </td>
                              <td>{formatCurrency(item.unit_price)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="text-right">
                    <span className="font-semibold">Total Qty Diterima: {totalQtyReceived()}</span>
                  </div>
                </>
              )}

              <div className="mt-6 flex gap-3 border-t border-surface-100 pt-4 dark:border-surface-800">
                <button type="button" onClick={() => { setShowCreateModal(false); setSelectedPO(null); setReceiptItems([]); }} className="btn-secondary flex-1">Batal</button>
                <button type="submit" disabled={saving || !selectedPO} className="btn-primary flex-1">{saving ? 'Menyimpan...' : 'Simpan Penerimaan'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedReceipt && (
        <div className="modal-overlay">
          <div className="bg-white/95 dark:bg-surface-900/95 rounded-3xl border border-white/80 dark:border-white/10 shadow-modal backdrop-blur-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Detail Penerimaan Barang</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label text-sm font-semibold">Nomor GR:</label>
                  <p className="text-gray-900 dark:text-white">{selectedReceipt.gr_number || selectedReceipt.number}</p>
                </div>
                <div>
                  <label className="label text-sm font-semibold">Tanggal:</label>
                  <p className="text-gray-900 dark:text-white">{formatDateTime(selectedReceipt.date)}</p>
                </div>
                <div>
                  <label className="label text-sm font-semibold">PO Number:</label>
                  <p className="text-gray-900 dark:text-white">{selectedReceipt.purchase_order?.po_number || selectedReceipt.po_number}</p>
                </div>
                <div>
                  <label className="label text-sm font-semibold">Status:</label>
                  <span className={`badge ${statusBadgeClass(selectedReceipt.status)}`}>{statusLabel(selectedReceipt.status)}</span>
                </div>
                <div>
                  <label className="label text-sm font-semibold">Supplier:</label>
                  <p className="text-gray-900 dark:text-white">{selectedReceipt.supplier?.name || selectedReceipt.purchase_order?.supplier?.name}</p>
                </div>
                <div>
                  <label className="label text-sm font-semibold">Gudang:</label>
                  <p className="text-gray-900 dark:text-white">{selectedReceipt.warehouse?.name || selectedReceipt.purchase_order?.warehouse?.name}</p>
                </div>
              </div>

              {selectedReceipt.items && selectedReceipt.items.length > 0 && (
                <div>
                  <h4 className="text-md font-semibold mb-3">Item Diterima</h4>
                  <div className="table-container">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Produk</th>
                          <th>SKU</th>
                          <th>Qty Dipesan</th>
                          <th>Qty Diterima</th>
                          <th>Harga Satuan</th>
                          <th>Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedReceipt.items.map((item, index) => (
                          <tr key={index}>
                            <td>{item.product_name || item.product?.name}</td>
                            <td>{item.sku || item.product?.sku}</td>
                            <td>{item.ordered_qty || item.qty}</td>
                            <td>{item.qty_received}</td>
                            <td>{formatCurrency(item.unit_price || item.price)}</td>
                            <td>{formatCurrency(item.subtotal || (item.qty_received * (item.unit_price || item.price)))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="mt-6 flex gap-3 border-t border-surface-100 pt-4 dark:border-surface-800">
                <button onClick={() => { setShowDetailsModal(false); setSelectedReceipt(null); }} className="btn-primary flex-1">Tutup</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}